import { useState, useEffect, useCallback } from 'react';
import { apiClient, storeClient, endpoints } from '../api/client';
import { resolveMarketplaceIdentifier } from '../api/storeMarketplaces';
import { BrandTraxListing } from '../types/brandtraxListing';

export interface ScrapingTask {
  id: string;
  name?: string;
  marketplace_definition_id: string;
  status: string;
  progress: number;
  total_urls: number;
  processed_urls: number;
  success_rate: number;
  parsing_success_rate?: number;
  max_pages?: number;
  max_stage2_products?: number;
  timeout_minutes?: number;
  urls?: string[];
  brand_name?: string;
  product_name?: string;
  input_mode?: 'urls' | 'keywords';
  keywords_text?: string;
  created_at: string;
  estimated_completion?: string;
  execution_time_seconds?: number | null;
  blocked_url?: string | null;
  screenshot_url?: string | null;
  challenge_diagnostics?: any;
  task_source?: string | null;
  crawl_request_id?: string | null;
  created_by?: string | null;
  execution_mode?: string | null;
  extension_any_marketplace?: boolean | null;
  extension_promoted_reason?: string | null;
  has_captcha?: boolean;
}

export interface ScrapingTaskCreate {
  name?: string;
  marketplace_definition_id: string;
  urls: string[];
  max_pages?: number;
  max_stage2_products?: number;
  timeout_minutes?: number;
  brand_name?: string;
  product_name?: string;
  input_mode?: 'urls' | 'keywords';
  keywords_text?: string;
  // Frontend hint to not start immediately (backend may ignore if unsupported)
  paused?: boolean;
  /** Store User.providerId (Azure AD oid) */
  created_by?: string;
  execution_mode?: 'internal' | 'extension';
  /** Let any extension worker claim this task, including unlisted marketplaces. */
  extension_any_marketplace?: boolean;
}

export type TaskSourceFilter =
  | 'all'
  | 'scraper_ui'
  | 'collector_extension'
  | 'importer_crawl_request'
  | 'dashboard_request'
  | 'speak_up_request'
  | 'definition_test_suite';

export const useScrapingTasks = (options?: {
  autoRefresh?: boolean;
  taskSourceFilter?: TaskSourceFilter;
  createdBy?: string | null;
}) => {
  const [tasks, setTasks] = useState<ScrapingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const toErrorMessage = (err: any): string => {
    try {
      const detail = err?.response?.data?.detail ?? err?.message ?? err;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        // e.g. FastAPI/Pydantic validation errors
        const first = detail[0];
        if (first && typeof first === 'object') {
          const loc = Array.isArray(first.loc) ? first.loc.join('.') : first.loc;
          return `${loc}: ${first.msg}`;
        }
        return detail.map((d: any) => (typeof d === 'string' ? d : JSON.stringify(d))).join('; ');
      }
      if (typeof detail === 'object') {
        if (detail.msg && detail.type) return `${detail.type}: ${detail.msg}`;
        return JSON.stringify(detail);
      }
      return String(detail);
    } catch (_) {
      return 'Unexpected error';
    }
  };

  const taskSourceFilter = options?.taskSourceFilter ?? 'all';
  const createdBy = options?.createdBy || null;

  const fetchTasks = useCallback(
    async (onSuccess?: () => void) => {
      try {
        if (initialLoading) setLoading(true);
        const params: Record<string, string | number> = { page: 1, size: 100 };
        if (taskSourceFilter !== 'all') {
          params.task_source = taskSourceFilter;
        }
        if (createdBy) {
          params.created_by = createdBy;
        }
        const response = await apiClient.get(endpoints.tasks, { params });
        setTasks(response.data.items || []);
        setError(null);
        if (onSuccess) onSuccess();
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to fetch tasks');
        console.error('Error fetching tasks:', err);
      } finally {
        if (initialLoading) setLoading(false);
        if (initialLoading) setInitialLoading(false);
      }
    },
    [initialLoading, taskSourceFilter, createdBy]
  );

  const createTask = useCallback(
    async (taskData: ScrapingTaskCreate): Promise<ScrapingTask | null> => {
      try {
        const response = await apiClient.post(endpoints.tasks, taskData);
        const newTask = response.data;

        // Add to local state
        setTasks(prev => [newTask, ...prev]);

        return newTask;
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to create task');
        console.error('Error creating task:', err);
        return null;
      }
    },
    []
  );

  const getTask = useCallback(async (taskId: string): Promise<ScrapingTask | null> => {
    try {
      const response = await apiClient.get(endpoints.task(taskId));
      return response.data;
    } catch (err: any) {
      setError(toErrorMessage(err) || 'Failed to fetch task');
      console.error('Error fetching task:', err);
      return null;
    }
  }, []);

  const getTaskResults = useCallback(async (taskId: string) => {
    try {
      const response = await apiClient.get(endpoints.taskResults(taskId));
      const data = response.data;

      // Normalize response shape and fields to match UI expectations
      const normalizeItem = (r: any) => ({
        id: r.id,
        url: r.url,
        parse_success: r.parse_success ?? r.success ?? false,
        success: r.success, // Preserve the original success field
        response_time: r.response_time ?? 0,
        html_content: r.html_content,
        html_size: r.html_size ?? 0,
        extracted_data: r.extracted_data ?? {},
        listings: r.listings, // Preserve the listings field
        error_message: r.error_message ?? null,
        crawled_at: r.crawled_at ?? r.created_at ?? '',
        status_code: r.status_code,
        // Surface screenshot_url for result-level thumbnail
        screenshot_url: r.screenshot_url || r.extracted_data?.screenshot_url || undefined,
        screenshot_timestamp: r.screenshot_timestamp || undefined,
      });

      if (Array.isArray(data)) {
        return { items: data.map(normalizeItem) };
      }
      if (data && Array.isArray(data.items)) {
        return { items: data.items.map(normalizeItem) };
      }
      return { items: [] };
    } catch (err: any) {
      setError(toErrorMessage(err) || 'Failed to fetch task results');
      console.error('Error fetching task results:', err);
      return null;
    }
  }, []);

  const getReviews = useCallback(
    async (
      brand?: string,
      stage?: string,
      limit: number = 50,
      offset: number = 0,
      q?: string,
      marketplaceIds?: string[],
      product?: string
    ) => {
      try {
        const url = endpoints.reviews(brand, stage, limit, offset, q, marketplaceIds, product);
        const response = await apiClient.get(url);
        const data = response.data || { items: [] };
        return {
          items: (data.items || []).map((it: any) => ({
            id: it.id,
            task_id: it.task_id,
            url: it.url,
            crawled_at: it.crawled_at,
            brand_name: it.brand_name,
            product_name: it.product_name,
            products: it.products || [],
          })),
        };
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to fetch reviews');
        console.error('Error fetching reviews:', err);
        return { items: [] };
      }
    },
    []
  );

  const rerunTask = useCallback(
    async (taskId: string, options?: { refresh?: boolean }): Promise<string | null> => {
      try {
        const response = await apiClient.post(`${endpoints.task(taskId)}/retry`);
        const newId = response.data?.data?.new_task_id as string | undefined;
        if (options?.refresh !== false) {
          fetchTasks();
        }
        return newId || null;
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to rerun task');
        console.error('Error rerunning task:', err);
        return null;
      }
    },
    [fetchTasks]
  );

  const startTask = useCallback(
    async (taskId: string, options?: { refresh?: boolean }): Promise<boolean> => {
      try {
        await apiClient.post(`${endpoints.task(taskId)}/start`);
        if (options?.refresh !== false) {
          fetchTasks();
        }
        return true;
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to start task');
        console.error('Error starting task:', err);
        return false;
      }
    },
    [fetchTasks]
  );

  const updateTask = useCallback(
    async (taskId: string, taskData: Partial<ScrapingTaskCreate>): Promise<ScrapingTask | null> => {
      try {
        const response = await apiClient.put(endpoints.task(taskId), taskData);
        const updatedTask = response.data;

        // Update local state
        setTasks(prev => prev.map(task => (task.id === taskId ? updatedTask : task)));

        return updatedTask;
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to update task');
        console.error('Error updating task:', err);
        return null;
      }
    },
    []
  );

  const deletePendingTasks = useCallback(async (): Promise<number | null> => {
    try {
      const response = await apiClient.delete(endpoints.deletePendingTasks);
      const deleted = Number(response.data?.data?.deleted ?? 0);
      await fetchTasks();
      return Number.isFinite(deleted) ? deleted : 0;
    } catch (err: any) {
      setError(toErrorMessage(err) || 'Failed to delete pending tasks');
      console.error('Error deleting pending tasks:', err);
      return null;
    }
  }, [fetchTasks]);

  const deleteTask = useCallback(async (taskId: string): Promise<boolean> => {
    try {
      await apiClient.delete(endpoints.task(taskId));
      // Optimistically update local state
      setTasks(prev => prev.filter(t => t.id !== taskId));
      return true;
    } catch (err: any) {
      setError(toErrorMessage(err) || 'Failed to delete task');
      console.error('Error deleting task:', err);
      return false;
    }
  }, []);

  const injectListing = useCallback(
    async (listing: BrandTraxListing): Promise<BrandTraxListing | null> => {
      try {
        const response = await storeClient.post(endpoints.storeListings, listing);
        return response.data;
      } catch (err: any) {
        setError(toErrorMessage(err) || 'Failed to inject listing to store');
        console.error('Error injecting listing:', err);
        return null;
      }
    },
    []
  );

  const injectSeller = useCallback(async (seller: any): Promise<any | null> => {
    try {
      const payload = { ...seller };
      const looksLikeDomain = (v: any) =>
        typeof v === 'string' && (v.includes('.') || v.startsWith('http'));
      // If marketplace is missing OR looks like a domain, resolve identifier from URL
      if (!payload.marketplace || looksLikeDomain(payload.marketplace)) {
        const id = await resolveMarketplaceIdentifier(payload.storeUrl || payload.url || '');
        if (id) payload.marketplace = id;
      }
      // Normalize to uppercase underscore style if possible (backend expects identifiers like AMAZON_US)
      if (typeof payload.marketplace === 'string') {
        payload.marketplace = String(payload.marketplace).trim();
      }
      const response = await storeClient.post(endpoints.storeSellers, payload);
      return response.data;
    } catch (err: any) {
      setError(toErrorMessage(err) || 'Failed to inject seller to store');
      console.error('Error injecting seller:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchTasks();

    if (options?.autoRefresh) {
      // Auto-refresh every 5 seconds to show real-time task updates
      const interval = setInterval(() => {
        fetchTasks();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchTasks, options?.autoRefresh]);

  return {
    tasks,
    loading,
    initialLoading,
    error,
    fetchTasks,
    createTask,
    updateTask,
    getTask,
    getTaskResults,
    rerunTask,
    startTask,
    deleteTask,
    deletePendingTasks,
    getReviews,
    injectListing,
    injectSeller,
  };
};
